import React, { useEffect, useState } from "react";
import { NavLink } from "react-router-dom";
import styled from "styled-components";
import PropTypes from "prop-types";
import { useLocation } from "react-router-dom";

const LinkItem = styled(NavLink)`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: ${({ $collapsed }) => ($collapsed ? "center" : "flex-start")};
  margin-bottom: 4px;
  padding: ${({ $collapsed }) => ($collapsed ? "0 4px" : "0 10px")};
  height: 44px;
  border-radius: 10px;
  overflow: hidden;
  text-decoration: none;
  color: ${({ color }) => color} !important;
  transition: background-color 0.2s ease, color 0.2s ease;

  &:hover:not(.active) {
    background-color: rgba(0, 150, 136, 0.12);
    color: #009688;
  }

  &:hover:not(.active) * {
    color: #009688;
  }

  &.active {
    background-color: ${({ activelinkbgcolor }) => activelinkbgcolor};
    color: ${({ coloronactive }) => coloronactive};
    box-shadow: inset 3px 0 0 ${({ activelinkaccent }) => activelinkaccent || "#009688"};
  }

  &.active * {
    color: ${({ coloronactive }) => coloronactive};
  }

  * {
    color: inherit;
  }
`;

const IconWrap = styled.div`
  width: ${({ $collapsed }) => ($collapsed ? "100%" : "36px")};
  min-width: ${({ $collapsed }) => ($collapsed ? "auto" : "36px")};
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const LabelText = styled.p`
  font-size: 13px;
  font-weight: 600;
  line-height: 1.2;
  margin: 0 0 0 4px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const SidebarItem = ({
  linkItem,
  activeLinkColor = "#ffffff",
  activeLinkBgColor = "#009688",
  colorOnHover = "#009688",
  linkColor = "#111827",
  openedMenu,
  inDossier,
  dossier,
}) => {
  const { path, label } = linkItem;
  const [show, setShow] = useState(false);
  const pathname = useLocation().pathname;
  const isCollapsed = !!openedMenu;

  useEffect(() => {
    if (linkItem.multiple) {
      const list = linkItem.sous_links.map((element) => {
        if (inDossier) {
          return `/client/${dossier}/` + element.path;
        }
        return element.path;
      });

      setShow(list.includes(pathname));
    }
  }, [pathname, linkItem, inDossier, dossier]);

  if (linkItem.multiple) {
    return (
      <div
        onClick={(e) => {
          e.stopPropagation();
          if (!isCollapsed) setShow((c) => !c);
        }}
        style={{
          background: show && !isCollapsed ? "rgba(0, 96, 88, 0.85)" : "transparent",
          borderRadius: show && !isCollapsed ? 10 : 0,
          marginBottom: show && !isCollapsed ? 8 : 0,
        }}
      >
        <Lien
          linkItem={linkItem}
          activeLinkColor={activeLinkColor}
          activeLinkBgColor={activeLinkBgColor}
          colorOnHover={colorOnHover}
          linkColor={linkColor}
          isCollapsed={isCollapsed}
          path={path}
          label={label}
          forceLightText={show && !isCollapsed}
        />
        {!isCollapsed && show && (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              paddingLeft: 12,
              color: "white",
            }}
          >
            {linkItem.sous_links.map((link, i) => (
              <div key={i} onClick={(e) => e.stopPropagation()}>
                <Lien
                  linkItem={link}
                  activeLinkColor={activeLinkColor}
                  activeLinkBgColor={activeLinkBgColor}
                  colorOnHover={colorOnHover}
                  linkColor="#ffffff"
                  isCollapsed={false}
                  path={link.path}
                  label={link.label}
                  forceLightText
                />
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  return (
    <Lien
      linkItem={linkItem}
      activeLinkColor={activeLinkColor}
      activeLinkBgColor={activeLinkBgColor}
      colorOnHover={colorOnHover}
      linkColor={linkColor}
      isCollapsed={isCollapsed}
      path={path}
      label={label}
    />
  );
};

SidebarItem.propTypes = {
  linkItem: PropTypes.shape({
    path: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    icon: PropTypes.elementType.isRequired,
  }).isRequired,
  activeLinkColor: PropTypes.string,
  activeLinkBgColor: PropTypes.string,
  colorOnHover: PropTypes.string,
  linkColor: PropTypes.string,
  openedMenu: PropTypes.bool,
  inDossier: PropTypes.bool,
  dossier: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

export default SidebarItem;

const Lien = ({
  linkItem,
  activeLinkColor,
  activeLinkBgColor,
  linkColor,
  isCollapsed,
  path,
  label,
  forceLightText = false,
}) => {
  const textColor = forceLightText ? "#fff" : undefined;

  return (
    <LinkItem
      className="sidebar-nav-link"
      color={textColor || linkColor || "#111827"}
      coloronactive={activeLinkColor}
      activelinkbgcolor={activeLinkBgColor}
      activelinkaccent="#00796b"
      $collapsed={isCollapsed}
      to={path}
      title={isCollapsed ? label : undefined}
    >
      <IconWrap $collapsed={isCollapsed}>
        <linkItem.icon size={20} className="icon" color="currentColor" />
      </IconWrap>
      {!isCollapsed && (
        <LabelText style={textColor ? { color: textColor } : undefined}>{label}</LabelText>
      )}
    </LinkItem>
  );
};
